import React from 'react';
import EmptyState from '../shared/EmptyState';
import LoadingSpinner from '../shared/LoadingSpinner';
import OrganizationRow from './OrganizationRow';
import PaginationControls from '../users/PaginationControls';

/**
 * OrganizationsTable - Table component for displaying organizations directory
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isLoading - Whether data is loading
 * @param {Array} props.organizations - Array of organization objects to display
 * @param {Array} props.filteredOrganizations - Filtered organizations for count display
 * @param {Array} props.paginatedOrganizations - Paginated organizations to display in table
 * @param {number} props.currentPage - Current page number
 * @param {number} props.totalPages - Total number of pages
 * @param {number} props.itemsPerPage - Items per page for pagination
 * @param {Array} props.users - All users for finding root admin info
 * @param {string|null} props.processingAction - ID of organization currently being processed
 * @param {Function} props.onAssignRootAdmin - Called when assign root admin button is clicked
 * @param {Function} props.onDeleteOrganization - Called when delete button is clicked
 * @param {Function} props.onPageChange - Called when page changes
 * @returns {React.ReactElement} Organizations table component
 */
const OrganizationsTable = ({
  isLoading,
  organizations,
  filteredOrganizations,
  paginatedOrganizations,
  currentPage,
  totalPages,
  itemsPerPage,
  users,
  processingAction,
  onAssignRootAdmin,
  onDeleteOrganization,
  onPageChange
}) => {
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (filteredOrganizations.length === 0) {
    return (
      <EmptyState
        iconClass="fa-solid fa-building-circle-exclamation"
        iconSize="2.5rem"
        message="No organizations registered in the directory."
        variant="card"
      />
    );
  }

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
              <th style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Organization Name</th>
              <th style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Unique ID</th>
              <th style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Root Administrator</th>
              <th style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'center' }}>Members</th>
              <th style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrganizations.map((t, idx) => (
              <OrganizationRow
                key={t.id}
                organization={t}
                index={idx}
                totalRows={paginatedOrganizations.length}
                users={users}
                processingAction={processingAction}
                onAssignRootAdmin={onAssignRootAdmin}
                onDeleteOrganization={onDeleteOrganization}
              />
            ))}
          </tbody>
        </table>
      </div>
      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filteredOrganizations.length}
        itemsPerPage={itemsPerPage}
        itemType="organizations"
        onPrevious={() => onPageChange(currentPage - 1)}
        onNext={() => onPageChange(currentPage + 1)}
      />
    </>
  );
};

export default OrganizationsTable;